import { useState, useEffect, useCallback } from "react";
import { useSubscription } from "@apollo/client";
import {
  NOW_PLAYING_CHANGED,
  screenService,
} from "../api/services/screenService";

export interface NowPlayingState {
  creativeId: string | null;
  creativeName: string | null;
  creativeType: string | null;
  fileUrl: string | null;
  duration: number | null;
  startedAt: string | null;
}

interface NowPlayingChangedEvent {
  screenId: string;
  creativeId: string | null;
  creativeName: string | null;
  creativeType: string | null;
  fileUrl: string | null;
  duration: number | null;
  startedAt: string | null;
}

export const useNowPlayingSubscription = (
  screenId?: string,
  enabled: boolean = true,
): NowPlayingState | null => {
  const [nowPlaying, setNowPlaying] = useState<NowPlayingState | null>(null);

  const fetchNowPlaying = useCallback(
    (id: string) => {
      screenService
        .getNowPlaying(id)
        .then((screen) => {
          if (screen?.nowPlayingCreativeId) {
            setNowPlaying({
              creativeId: screen.nowPlayingCreativeId,
              creativeName: screen.nowPlayingCreativeName ?? null,
              creativeType: screen.nowPlayingType ?? null,
              fileUrl: screen.nowPlayingFileUrl ?? null,
              duration: screen.nowPlayingDuration ?? null,
              startedAt: screen.nowPlayingStartedAt ?? null,
            });
          } else {
            setNowPlaying(null);
          }
        })
        .catch((err) => {
          console.error("[NowPlaying] Failed to fetch state:", err);
        });
    },
    [],
  );

  // Fetch the current state once when the subscription becomes enabled.
  // Live updates after that come from the nowPlayingChanged subscription.
  useEffect(() => {
    if (!screenId || !enabled) {
      setNowPlaying(null);
      return;
    }

    fetchNowPlaying(screenId);
  }, [screenId, enabled, fetchNowPlaying]);

  useSubscription<{ nowPlayingChanged: NowPlayingChangedEvent }>(
    NOW_PLAYING_CHANGED,
    {
      skip: !screenId || !enabled,
      variables: { screenId },
      onData: ({ data }) => {
        const event = data?.data?.nowPlayingChanged;
        if (!event) return;

        if (event.creativeId) {
          setNowPlaying({
            creativeId: event.creativeId,
            creativeName: event.creativeName,
            creativeType: event.creativeType,
            fileUrl: event.fileUrl,
            duration: event.duration,
            startedAt: event.startedAt,
          });
        } else {
          setNowPlaying(null);
        }
      },
      onError: (error) => {
        console.error("[NowPlayingSubscription] Error:", error.message);
      },
    },
  );

  return nowPlaying;
};
